/* Hallmark · component: MultiSelect · genre: modern-minimal · theme: maeiz-tokens
 * Checkbox multi-select with search; collision-aware popover
 */
import * as Popover from "@radix-ui/react-popover";
import React, { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Button } from "../Button";
import { fieldSurfaceClass } from "../../styles/field";
import { overlayCollision, overlayContentMotionClass, thinScrollbarClass } from "../../styles/overlay";

export interface MultiSelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface MultiSelectProps {
    label?: string | React.ReactNode;
    error?: string;
    helperText?: string;
    fullWidth?: boolean;
    placeholder?: string;
    options: MultiSelectOption[];
    value: (string | number)[];
    onChange: (value: (string | number)[]) => void;
    searchable?: boolean;
    searchPlaceholder?: string;
    className?: string;
    id?: string;
    disabled?: boolean;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
    (
        {
            label,
            error,
            helperText,
            fullWidth = false,
            placeholder = "Select options…",
            options,
            value = [],
            onChange,
            searchable = true,
            searchPlaceholder = "Search…",
            className,
            id,
            disabled = false,
        },
        ref,
    ) => {
        const [open, setOpen] = useState(false);
        const [query, setQuery] = useState("");
        const selectId = id || `multiselect-${Math.random().toString(36).slice(2, 9)}`;

        const filtered = useMemo(() => {
            const q = query.trim().toLowerCase();
            if (!q) return options;
            return options.filter((o) => o.label.toLowerCase().includes(q));
        }, [options, query]);

        const selected = useMemo(() => options.filter((o) => value.includes(o.value)), [options, value]);

        const toggle = (v: string | number) => {
            if (disabled) return;
            onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
        };

        return (
            <div className={clsx("flex flex-col gap-1.5", fullWidth && "w-full", className)}>
                {label ? (
                    <label htmlFor={selectId} className="text-sm font-medium text-neutral-dark dark:text-white">
                        {label}
                    </label>
                ) : null}
                <Popover.Root
                    open={open}
                    onOpenChange={(next) => {
                        setOpen(next);
                        if (!next) setQuery("");
                    }}
                >
                    <Popover.Trigger asChild disabled={disabled}>
                        <Button
                            ref={ref}
                            id={selectId}
                            type="button"
                            variant="outline"
                            disabled={disabled}
                            aria-expanded={open}
                            className={clsx(
                                "h-auto min-h-11 w-full justify-between py-2 font-normal",
                                fieldSurfaceClass({ error: !!error, disabled }),
                                "border-primary/80 dark:border-gold/60",
                            )}
                            icon={ChevronDown}
                            iconPosition="end"
                        >
                            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-start">
                                {selected.length === 0 ? (
                                    <span className="text-neutral-secondary">{placeholder}</span>
                                ) : (
                                    selected.map((opt) => (
                                        <span
                                            key={String(opt.value)}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary dark:bg-gold/15 dark:text-gold-light"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {opt.label}
                                            {!disabled ? (
                                                <button
                                                    type="button"
                                                    className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
                                                    aria-label={`Remove ${opt.label}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggle(opt.value);
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            ) : null}
                                        </span>
                                    ))
                                )}
                            </span>
                        </Button>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content
                            align="start"
                            sideOffset={6}
                            avoidCollisions={overlayCollision.avoidCollisions}
                            collisionPadding={overlayCollision.collisionPadding}
                            className={clsx(
                                "z-[80] w-[var(--radix-popover-trigger-width)] min-w-[240px] overflow-hidden rounded-xl border border-neutral-border bg-white shadow-mz-lg dark:border-white/12 dark:bg-neutral-navy",
                                overlayContentMotionClass,
                            )}
                            onOpenAutoFocus={(e) => {
                                e.preventDefault();
                                (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>("input")?.focus();
                            }}
                        >
                            {searchable ? (
                                <div className="flex items-center gap-2 border-b border-neutral-border px-3 py-2.5 dark:border-white/10">
                                    <Search size={16} className="text-primary dark:text-gold-light" aria-hidden />
                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={searchPlaceholder}
                                        className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-secondary dark:text-white"
                                    />
                                </div>
                            ) : null}
                            <ul className={clsx("max-h-56 p-1", thinScrollbarClass)} role="listbox" aria-multiselectable>
                                {filtered.length === 0 ? (
                                    <li className="px-2.5 py-3 text-sm text-neutral-secondary">No matches</li>
                                ) : (
                                    filtered.map((opt) => {
                                        const isOn = value.includes(opt.value);
                                        return (
                                            <li key={String(opt.value)}>
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isOn}
                                                    disabled={opt.disabled}
                                                    className={clsx(
                                                        "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start text-sm",
                                                        "hover:bg-neutral-light disabled:opacity-40 dark:hover:bg-white/10",
                                                        isOn && "bg-primary/5 dark:bg-gold/10",
                                                    )}
                                                    onClick={() => toggle(opt.value)}
                                                >
                                                    <span
                                                        className={clsx(
                                                            "flex h-[18px] w-[18px] items-center justify-center rounded border-2",
                                                            isOn
                                                                ? "border-primary bg-primary text-white dark:border-gold dark:bg-gold dark:text-neutral-ink"
                                                                : "border-neutral-border dark:border-white/25",
                                                        )}
                                                    >
                                                        {isOn ? <Check size={12} strokeWidth={3} /> : null}
                                                    </span>
                                                    {opt.label}
                                                </button>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
                {error ? (
                    <p className="text-sm text-danger" role="alert">
                        {error}
                    </p>
                ) : null}
                {helperText && !error ? <p className="text-sm text-neutral-secondary">{helperText}</p> : null}
            </div>
        );
    },
);

MultiSelect.displayName = "MultiSelect";
